import { useState } from 'react';
import {
  Info,
  CheckCircle,
  ArrowRight,
  Database,
  Calculator,
  Battery,
  DollarSign,
  AlertCircle,
  BarChart3
} from 'lucide-react';
import {
  DataPreparationStep,
  TheoreticalMaximumStep,
  BESSOptimizationStep,
  SizeOptimizationStep,
  EconomicAnalysisStep
} from './algorithm-steps';

interface AlgorithmSectionProps {
  bill: any;
  configuration: any;
  consumption: any;
}

export function AlgorithmSection({ bill, configuration, consumption }: AlgorithmSectionProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="space-y-6">
      {/* Algorithm Overview - Clean Apple Style */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <h3 className="text-base font-semibold text-black flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-brand-primary" />
            </div>
            BESS Optimization Algorithm
          </h3>
        </div>
        <div className="p-6">
          <div className="text-sm text-gray-600">
            <p className="mb-4">
              Our BESS optimization uses a <span className="font-medium text-gray-900">modular architecture</span> that separates
              planning strategies from simulation execution. This flexible approach allows for different optimization methods
              while maintaining consistent performance evaluation and real-world constraint validation.
            </p>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Important Note</p>
              <p className="text-sm text-gray-600">
                Values shown depend on the data period used. With overlap period only (Apr-Jul 2024),
                theoretical maximum is ~$206k. With extended data (full year), values increase proportionally.
              </p>
            </div>
          </div>

          {/* High-Level Flow - Clean Apple Style */}
          <div className="bg-gray-50 p-4 rounded-lg mt-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">ALGORITHM PIPELINE</p>
            <div className="flex items-center justify-between gap-2 text-xs">
              <div className="flex-1 text-center">
                <div className="bg-white border border-gray-200 py-2 px-3 rounded-lg">
                  <Database className="w-4 h-4 mx-auto mb-1 text-gray-600" />
                  <span className="text-gray-900 font-medium">1. Data Alignment</span>
                </div>
                <p className="mt-1 text-gray-500">15-min → Hourly</p>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <div className="flex-1 text-center">
                <div className="bg-white border border-gray-200 py-2 px-3 rounded-lg">
                  <Calculator className="w-4 h-4 mx-auto mb-1 text-gray-600" />
                  <span className="text-gray-900 font-medium">2. Theoretical Max</span>
                </div>
                <p className="mt-1 text-gray-500">Perfect Foresight</p>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <div className="flex-1 text-center">
                <div className="bg-white border border-gray-200 py-2 px-3 rounded-lg">
                  <Battery className="w-4 h-4 mx-auto mb-1 text-gray-600" />
                  <span className="text-gray-900 font-medium">3. Plan & Simulate</span>
                </div>
                <p className="mt-1 text-gray-500">Modular Execution</p>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <div className="flex-1 text-center">
                <div className="bg-white border border-gray-200 py-2 px-3 rounded-lg">
                  <DollarSign className="w-4 h-4 mx-auto mb-1 text-gray-600" />
                  <span className="text-gray-900 font-medium">4. Economics</span>
                </div>
                <p className="mt-1 text-gray-500">NPV/IRR/Payback</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step Components */}
      <DataPreparationStep
        expandedSection={expandedSection}
        toggleSection={toggleSection}
        bill={bill}
        consumption={consumption}
      />

      <TheoreticalMaximumStep
        expandedSection={expandedSection}
        toggleSection={toggleSection}
      />

      <BESSOptimizationStep
        expandedSection={expandedSection}
        toggleSection={toggleSection}
      />

      <SizeOptimizationStep
        expandedSection={expandedSection}
        toggleSection={toggleSection}
      />

      <EconomicAnalysisStep
        expandedSection={expandedSection}
        toggleSection={toggleSection}
      />

      {/* Algorithm Performance Summary */}
      <div className="bg-white rounded-2xl border-2 border-brand-primary shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-brand-primary/20 bg-brand-primary/5">
          <h3 className="text-lg font-semibold text-gray-900">Algorithm Performance Summary</h3>
        </div>
        <div className="p-6 bg-brand-primary/5">
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-lg">
              <p className="text-sm font-semibold text-gray-900 mb-3">Performance Analysis - Data Period Dependent</p>

              <div className="bg-gray-50 border border-gray-200 rounded p-2 mb-3">
                <p className="text-xs text-gray-700">
                  <AlertCircle className="w-3 h-3 inline mr-1" />
                  Values vary based on data period used. Shown here: overlap period (Apr-Jul 2024)
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-600 mb-2">OVERLAP PERIOD ONLY</p>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Total Consumption:</span>
                      <span className="font-bold">2,953 MWh</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Cost @ $70/MWh:</span>
                      <span className="font-bold">$206,680</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Theoretical Max:</span>
                      <span className="font-bold">~$206,000</span>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-600 mb-2">WITH DATA EXTENSION</p>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Extended to:</span>
                      <span className="font-bold">7,834 MWh</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Cost @ $70/MWh:</span>
                      <span className="font-bold">$548,352</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Theoretical Max:</span>
                      <span className="font-bold">$298,344</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t">
                <p className="text-xs text-gray-600">
                  The algorithm performs excellently within each scenario, but absolute values
                  depend on whether original or extended data is used.
                </p>
              </div>
            </div>

            <div className="bg-gray-100 border border-gray-300 rounded-lg p-3">
              <p className="text-xs font-semibold text-gray-900 mb-1">
                <CheckCircle className="w-3 h-3 inline mr-1" />
                KEY ACHIEVEMENT
              </p>
              <p className="text-xs text-gray-700">
                The algorithm performs optimally for the given constraints, effectively identifying and
                exploiting price arbitrage opportunities. Performance depends on data quality, coverage period,
                and BESS sizing. With sufficient battery capacity, the algorithm captures most available value.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Technical Implementation Notes */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <h3 className="text-lg font-semibold text-gray-900">Technical Implementation Notes</h3>
        </div>
        <div className="p-6">
          <div className="space-y-3 text-xs">
            <div className="flex items-start gap-2">
              <Info className="w-3 h-3 text-brand-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Data Structures</p>
                <p className="text-gray-600">
                  Consumption data: 15-min intervals in kWh. Price data: Hourly in $/MWh.
                  All timestamps in ISO 8601 format.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Info className="w-3 h-3 text-brand-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Computational Complexity</p>
                <p className="text-gray-600">
                  O(n log n) for sorting operations, O(n) for simulation. Total runtime ~100ms for full year.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Info className="w-3 h-3 text-brand-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Grid Export Feature</p>
                <p className="text-gray-600">
                  Allows selling excess energy back to grid during high-price periods, increasing revenue by ~15%.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Info className="w-3 h-3 text-brand-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Future Enhancements</p>
                <p className="text-gray-600">
                  Planned: Machine learning price forecasting, demand charge optimization, multi-site coordination.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}